import type {FunctionComponent} from 'react'
import React from 'react'
import {useOutletContext, useRouteLoaderData} from '@remix-run/react'
import clsx from 'clsx'
import ShareMenu from '~/components/commons/share-menu'
import {getTranslation} from '~/localization/translation'
import type {RootAndListContext, RootLoaderResponse} from '~/types'
import type {DomainConfig} from '~/domains'
import {getDomainConfig} from '~/domains'
import BreadcrumbsHeaderMaga from '../breadcrumbs/breadcrumbs-header-maga'
import AffiliateDisclosureMaga from './affiliate-disclosure-maga'

const heroImageBackdropDomainNames = [
  'topstatesreviews',
  'americanbest10',
  'canadabest10',
] as DomainConfig['domainName'][]

const MagaListHeader: FunctionComponent = () => {
  const {
    dataResponse: {products, productsCategory},
    displayCookieConsent,
  } = useOutletContext<RootAndListContext>()
  const {language, heroImage, deviceType, isUSLocale, domain, locale} = useRouteLoaderData('root') as RootLoaderResponse
  const translations = getTranslation(language)
  const {domainName, styles: stylesBydomain} = getDomainConfig(domain, locale)
  const shouldHaveLowerBrightness = heroImageBackdropDomainNames.includes(domainName)

  return (
    <div
      className="relative mb-7 flex h-[450px] flex-col py-5 tablet:mb-5 tablet:max-h-fit tablet:min-h-[295px] tablet:py-3"
      data-testid="list-header"
    >
      <picture>
        <source srcSet={heroImage?.desktop} media="(min-width: 1024px)" />
        <source srcSet={heroImage?.smartphone} media="(max-width: 1023px)" />
        <img
          src={heroImage?.smartphone}
          alt="Hero"
          className={clsx(
            'absolute inset-0 z-20 h-full w-full object-cover',
            shouldHaveLowerBrightness && 'brightness-50',
          )}
          fetchpriority="high"
          decoding="async"
          loading="eager"
        />
      </picture>

      <div
        className={clsx(
          'mx-auto flex w-full flex-col justify-center',
          stylesBydomain.UTILS_MAIN_CONTAINER,
          stylesBydomain.LISTPAGE_HERO_IMAGE_HEADER_TEXT,
          'listpage_hero_image_header_text',
          'utils_main_container',
        )}
      >
        {isUSLocale ? (
          <BreadcrumbsHeaderMaga />
        ) : (
          <div
            className={displayCookieConsent && deviceType && deviceType !== 'desktop' ? 'invisible h-6' : 'hidden'}
          />
        )}
        <h1 className="z-20 mt-5 text-center text-[64px] font-[500] capitalize leading-[120%] tablet:text-4xl">
          {translations.Header.titleMagaTop(products?.slice(0, 10)?.length, productsCategory?.name)}
          <br />
          {translations.Header.titleMagaLoved}
        </h1>
        <span className="z-20 mx-auto mt-3 block max-w-80 text-center text-base font-normal">
          {translations.Header.subtitleMaga}
        </span>
      </div>
      <div
        className={clsx(
          'mx-auto mt-auto flex flex-row items-center justify-center gap-28 tablet:justify-between',
          stylesBydomain.UTILS_MAIN_CONTAINER,
          'utils_main_container',
        )}
      >
        <AffiliateDisclosureMaga />
        <ShareMenu
          onHover={() => null}
          className="z-30 flex flex-row-reverse gap-5 [&>div>button>.share-icon]:!size-8 [&>div>button>.share-icon]:p-1.5 [&>div>button>svg]:shadow-lg [&>div]:!mr-0 [&>div]:flex [&>div]:items-center"
          placement="maga-list-header"
          shouldShowNewIcon={true}
        />
      </div>
    </div>
  )
}

export default MagaListHeader
